/**
 * Org chart view on /team-roster (Oct 27, 2026). Rules in src/lib/orgChart.ts.
 *
 * Desktop (lg+): boxes and lines, top down. People with no one under them sit
 * in a stacked column under their boss (two columns past six) so a team lead's
 * twelve RMs don't stretch the chart sideways.
 * Phone: an indented outline with fold-up branches.
 * Tapping a person opens the roster editor; the chart never moves anyone.
 */

import { useMemo, useState } from "react";
import {
  buildOrgTree, firstName, LINE_BY_PAY_ROLES, ROLE_LABEL,
  type OrgMember, type OrgNode,
} from "../../lib/orgChart";

const SALES_ROLES = new Set(["sales_director", "asm", "team_lead", "restore_manager", "canvasser"]);
const OPS_ROLES = new Set([
  "director_of_operations", "roof_director", "roof_team_manager", "roof_specialist",
  "report_specialist", "drone_pilot", "qc", "project_manager",
]);

function accentClass(role: string): string {
  if (role === "admin") return "border-t-bardin-ink";
  if (SALES_ROLES.has(role)) return "border-t-bardin-orange";
  if (OPS_ROLES.has(role)) return "border-t-bardin-teal";
  return "border-t-violet-400";
}

function title(m: OrgMember): string {
  const main = ROLE_LABEL[m.role] ?? m.role;
  return m.secondary_role ? `${main} + ${ROLE_LABEL[m.secondary_role] ?? m.secondary_role}` : main;
}

function warningsFor(node: OrgNode): string[] {
  const m = node.member;
  const out = [...node.warnings];
  if (LINE_BY_PAY_ROLES.has(m.role) && !m.asm_id) out.unshift("no ASM");
  return out;
}

interface CardProps {
  node: OrgNode;
  q: string;
  compact?: boolean;
  onOpen: (id: string) => void;
}

function PersonCard({ node, q, compact, onOpen }: CardProps) {
  const m = node.member;
  const match = q.length > 0 && m.full_name.toLowerCase().includes(q);
  const warns = warningsFor(node);
  return (
    <button
      type="button"
      onClick={() => onOpen(m.id)}
      title={`${m.full_name} · ${title(m)} — tap to edit`}
      className={`${compact ? "w-40" : "w-44"} text-left bg-white border border-slate-200 ${compact ? "border-t-2" : "border-t-4"} ${accentClass(m.role)} rounded-lg ${compact ? "px-2.5 py-1.5" : "px-3 py-2 shadow-sm"} hover:shadow-md hover:border-slate-300 transition ${match ? "ring-2 ring-bardin-orange ring-offset-1" : ""}`}
    >
      <div className={`${compact ? "text-xs" : "text-sm"} font-bold text-bardin-ink truncate`}>{m.full_name}</div>
      <div className="text-[11px] text-slate-500 truncate">{title(m)}</div>
      {(m.on_leave || warns.length > 0 || node.size > 0) && (
        <div className="flex flex-wrap gap-1 mt-1">
          {node.size > 0 && (
            <span className="px-1.5 py-px rounded-full bg-slate-100 text-slate-600 text-[10px] font-semibold">
              {node.size} {node.size === 1 ? "person" : "people"}
            </span>
          )}
          {m.on_leave && (
            <span className="px-1.5 py-px rounded-full bg-sky-50 text-sky-700 text-[10px] font-semibold">On leave</span>
          )}
          {warns.map((w) => (
            <span key={w} title={w} className="max-w-full truncate px-1.5 py-px rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold">
              ⚠ {w}
            </span>
          ))}
        </div>
      )}
    </button>
  );
}

/* ------------------------------------------------------------------ */
/* Desktop: boxes and lines                                            */
/* ------------------------------------------------------------------ */

type Column = { kind: "branch"; node: OrgNode } | { kind: "leaves"; nodes: OrgNode[] };

const LINE = "bg-slate-300";

function TreeNode({ node, q, onOpen }: { node: OrgNode; q: string; onOpen: (id: string) => void }) {
  const branches = node.children.filter((c) => c.children.length > 0);
  const leaves = node.children.filter((c) => c.children.length === 0);
  const cols: Column[] = [
    ...branches.map((b): Column => ({ kind: "branch", node: b })),
    ...(leaves.length ? [{ kind: "leaves", nodes: leaves } as Column] : []),
  ];
  return (
    <div className="flex flex-col items-center">
      <PersonCard node={node} q={q} onOpen={onOpen} />
      {cols.length > 0 && (
        <>
          <span className={`w-px h-5 ${LINE}`} />
          <div className="flex items-start">
            {cols.map((col, i) => (
              <div key={col.kind === "branch" ? col.node.member.id : "leaves"} className="relative flex flex-col items-center px-1.5">
                {cols.length > 1 && (
                  <span
                    className={`absolute top-0 h-px ${LINE}`}
                    style={{ left: i === 0 ? "50%" : 0, right: i === cols.length - 1 ? "50%" : 0 }}
                  />
                )}
                <span className={`w-px h-5 ${LINE}`} />
                {col.kind === "branch" ? (
                  <TreeNode node={col.node} q={q} onOpen={onOpen} />
                ) : (
                  <div className={`grid gap-1.5 ${col.nodes.length > 6 ? "grid-cols-2" : "grid-cols-1"}`}>
                    {col.nodes.map((n) => (
                      <PersonCard key={n.member.id} node={n} q={q} compact onOpen={onOpen} />
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* Phone: indented outline                                             */
/* ------------------------------------------------------------------ */

function OutlineNode({ node, q, onOpen }: { node: OrgNode; q: string; onOpen: (id: string) => void }) {
  const [open, setOpen] = useState(true);
  const hasKids = node.children.length > 0;
  return (
    <div>
      <div className="flex items-start gap-1">
        {hasKids ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            aria-label={`${open ? "Fold up" : "Open"} ${firstName(node.member.full_name)}'s team`}
            className="w-6 h-7 mt-1.5 shrink-0 rounded text-slate-500 hover:bg-slate-100 text-base leading-none"
          >
            {open ? "▾" : "▸"}
          </button>
        ) : (
          <span className="w-6 shrink-0" />
        )}
        <div className="min-w-0 flex-1 [&>button]:w-full">
          <PersonCard node={node} q={q} onOpen={onOpen} />
        </div>
      </div>
      {hasKids && open && (
        <div className="ml-3 pl-2 border-l border-slate-200 mt-1.5 space-y-1.5">
          {node.children.map((c) => (
            <OutlineNode key={c.member.id} node={c} q={q} onOpen={onOpen} />
          ))}
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/* View                                                                */
/* ------------------------------------------------------------------ */

export default function OrgChart({ members, search, onOpen }: {
  members: OrgMember[];
  search: string;
  onOpen: (id: string) => void;
}) {
  const tree = useMemo(() => buildOrgTree(members), [members]);
  const q = search.trim().toLowerCase();
  const matches = q ? members.filter((m) => m.is_active && m.full_name.toLowerCase().includes(q)).length : 0;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] text-slate-500">
        <span>
          {tree.count} people · tap anyone to change their spot
          {q ? ` · ${matches} match${matches === 1 ? "" : "es"} highlighted` : ""}
        </span>
        <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-bardin-orange" /> Sales</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-bardin-teal" /> Operations</span>
        <span className="flex items-center gap-1"><span className="w-3 h-1 rounded bg-violet-400" /> Office &amp; finance</span>
      </div>

      {tree.top ? (
        <>
          <div className="hidden lg:block bg-slate-50/60 border border-slate-200 rounded-xl overflow-x-auto py-5">
            <div className="w-max mx-auto px-4">
              <TreeNode node={tree.top} q={q} onOpen={onOpen} />
            </div>
          </div>
          <div className="lg:hidden">
            <OutlineNode node={tree.top} q={q} onOpen={onOpen} />
          </div>
        </>
      ) : (
        <div className="text-sm text-slate-500 py-6 text-center">No reporting lines yet — set "Reports to" in each person's edit form.</div>
      )}

      {tree.notPlaced.length > 0 && (
        <section className="bg-amber-50/60 border border-amber-200 rounded-xl p-4">
          <h2 className="text-[11px] font-bold uppercase tracking-wider text-amber-700 mb-1">
            Not on the chart yet · {tree.notPlaced.length}
          </h2>
          <p className="text-[11px] text-amber-800/80 mb-3">
            Tap a person to set who they report to (RMs and team leads: their team lead and ASM).
          </p>
          <div className="space-y-3">
            {tree.notPlaced.map(({ node, reason }) => (
              <div key={node.member.id}>
                <OutlineNode node={node} q={q} onOpen={onOpen} />
                {/* A warning reason already shows as a badge on the card. */}
                {node.warnings.length === 0 && (
                  <p className="text-[11px] text-amber-700 ml-7 mt-0.5">{reason}</p>
                )}
              </div>
            ))}
          </div>
        </section>
      )}

      <p className="text-[11px] text-slate-400 leading-relaxed">
        RMs and team leads sit under their team lead and ASM — the same fields that pay overrides. Everyone else sits
        under their "Reports to", which is for this chart only and never changes pay.
      </p>
    </div>
  );
}
